import * as dateFns from "date-fns";
import { Link } from "@tanstack/react-router";
import { Dialog, Modal as RACModal, ModalOverlay } from "react-aria-components";
import { AlertCircle, MinusCircle } from "react-feather";
import {
  AppError,
  Loader,
  Tag,
  Tooltip,
  type TagTone,
} from "@bfc-software/pallet";
import type { TFunction } from "i18next";

import { fromQuery } from "../-data/async";
import { useTaskDetail } from "../-data/use-task-detail";
import type { PickLine, PickStatus, Task, TaskDetail } from "../-data/types";

import { NotReleasedMark, TaskStatusTag } from "./task-bits";
import sharedStyles from "./shared.module.css";
import styles from "./task-detail-drawer.module.css";

/**
 * The task detail drawer: what a task is made of - its meta and its pick lines.
 *
 * Hand-rolled on top of react-aria-components rather than pallet's `Modal` or
 * `SidebarOverlay`: both of those own their trigger (`trigger: ButtonProps`
 * rendered inside a `DialogTrigger`), because every existing caller opens them
 * from a button sitting right next to the dialog. This drawer's trigger is a
 * table row that may be scrolled out of view by the time the drawer closes,
 * and its open/closed state already lives in the URL (`useTaskDetailDrawer`) so
 * that a reload or a paste reopens it - a `DialogTrigger`'s own internal state
 * would be a second, competing answer to "is it open". `ModalOverlay`/`Dialog`
 * are the same primitives both pallet components build on, so this keeps the
 * focus-trap and escape/outside-dismiss behaviour without inheriting either
 * one's ownership of the trigger.
 */

/**
 * The three path params any link out of this drawer needs, handed down from
 * whichever tab rendered it rather than read here - the drawer is not a route
 * and has no `Route.useParams()` of its own.
 */
export interface WarehouseParams {
  lang: string;
  org: string;
  location: string;
}

/** `startedAt` bounds a claim time, not a shelf-life window, but the same
 * unparseable-falls-back-to-raw guard applies for the same reason. */
function formatStarted(value: string): string {
  const parsed = dateFns.parseISO(value);
  return dateFns.isValid(parsed) ? dateFns.format(parsed, "PP p") : value;
}

/** One meta grid cell: an uppercase label over its value. */
function MetaField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className={styles.metaField}>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{value}</span>
    </div>
  );
}

/**
 * Where a pick line's item id goes when clicked: the warehouse's Items page,
 * with this one item searched for.
 *
 * The items list has no per-item route to deep link to - the id goes into its
 * `q`, which is an exact `item_ext_id` lookup for anything shaped like an item
 * id and a description substring search otherwise (see `searchFilterParam` in
 * `items.tsx`). Every id here comes from `TaskDetailPick.item_id`, which *is*
 * that catalog id, so the search lands on the one row.
 *
 * `attrs` and `offset` are stated rather than left out: the items route's
 * search is a whole object, and carrying over an attribute filter or a page
 * offset from wherever the user last was would land them on a filtered page
 * two that may not hold the item at all.
 */
function ItemLink({
  params,
  itemId,
}: {
  params: WarehouseParams;
  itemId: string;
}) {
  return (
    <Link
      to="/$lang/orgs/$org/outbound/whs/$location/items"
      params={params}
      search={{ q: itemId, attrs: "", offset: 0 }}
      className={styles.pickItemLink}
      data-test-role="task-detail-pick-item-link"
    >
      {itemId}
    </Link>
  );
}

/**
 * How each pick status reads as a tone.
 *
 * Exhaustive over `PickStatus`, for the reason `STATUS_TONES` in
 * `task-bits.tsx` is exhaustive over `TaskStatus`: a member added to the union
 * fails here rather than rendering as an untoned chip nobody notices.
 *
 * `short` is `warning` and not `danger`. A short is a normal, expected outcome
 * of a pick - the item was not there in the quantity the plan asked for - and
 * the row is not an error to be fixed. `danger` in this palette is reserved for
 * something that went wrong, which is the tone the unknown case below carries.
 */
const PICK_STATUS_TONES: Record<PickStatus, TagTone> = {
  pending: "neutral",
  "in-progress": "info",
  picked: "success",
  short: "warning",
};

/** The i18n key under `tasks.taskDetail.drawer.pickStatus` for each status. */
const PICK_STATUS_LABEL_KEYS: Record<PickStatus, string> = {
  pending: "pending",
  "in-progress": "inProgress",
  picked: "picked",
  short: "short",
};

/**
 * The Status cell: a chip for a pick we have an answer for, an icon affordance
 * for the two kinds we do not.
 *
 * `unknown` and `not-released` are both drawn as icon + `Tooltip` + hidden
 * label rather than as a chip, and for the same reason `NotReleasedMark` in
 * `task-bits.tsx` states: every chip in this column is a claim about the
 * warehouse, and neither of these is one. But the two must not look alike -
 * `unknown` keeps `AlertCircle` in `danger`, because it means "something went
 * wrong" (the history read failed, or came back with no row for this pick, on
 * a task that *was* released), while `not-released` gets `MinusCircle`, the
 * same glyph `NotReleasedMark` draws in the table's Status column, because it
 * means "nothing has happened yet" and is not an error to flag. Confusing the
 * two would tell a manager a planned task's picks are broken, or a released
 * task's failed read is merely unstarted. Not `NotReleasedMark` itself,
 * though: that component's copy is the table's, and this cell's strings live
 * under `tasks.taskDetail.drawer.pickStatus` instead - same shape, own
 * wording.
 */
function PickStatusCell({ pick, t }: { pick: PickLine; t: TFunction }) {
  if (pick.execution.kind === "not-released") {
    return (
      <span
        className={styles.pickStatusNotReleased}
        data-test-role="task-detail-pick-status"
        data-status="not-released"
      >
        <Tooltip content={t("tasks.taskDetail.drawer.pickStatus.notReleased")}>
          <MinusCircle size={15} aria-hidden="true" />
          <span className={sharedStyles.srOnly}>
            {t("tasks.taskDetail.drawer.pickStatus.notReleasedLabel")}
          </span>
        </Tooltip>
      </span>
    );
  }

  if (pick.execution.kind === "unknown") {
    return (
      <span
        className={styles.pickStatusUnknown}
        data-test-role="task-detail-pick-status"
        data-status="unknown"
      >
        <Tooltip content={t("tasks.taskDetail.drawer.pickStatus.unknown")}>
          <AlertCircle size={15} aria-hidden="true" />
          <span className={sharedStyles.srOnly}>
            {t("tasks.taskDetail.drawer.pickStatus.unknownLabel")}
          </span>
        </Tooltip>
      </span>
    );
  }

  const status = pick.execution.status;

  return (
    // Tag stamps its own data-test-role, so the cell-level hook goes on a
    // wrapper - same arrangement as `TaskStatusTag`.
    <span data-test-role="task-detail-pick-status" data-status={status}>
      <Tag
        label={t(
          `tasks.taskDetail.drawer.pickStatus.${PICK_STATUS_LABEL_KEYS[status]}`,
        )}
        display={{ tone: PICK_STATUS_TONES[status] }}
      />
    </span>
  );
}

/** One row of the pick lines table. */
function PickLineRow({
  pick,
  params,
  t,
}: {
  pick: PickLine;
  params: WarehouseParams;
  t: TFunction;
}) {
  return (
    <tr className={styles.pickRow} data-test-role="task-detail-pick-row">
      <td className={styles.pickSeq}>{pick.seq}</td>
      <td className={styles.pickItem}>
        <ItemLink params={params} itemId={pick.itemId} />
        {pick.itemName ? (
          <div className={styles.pickItemName}>{pick.itemName}</div>
        ) : (
          <></>
        )}
      </td>
      <td className={styles.pickSlot}>{pick.slotId ?? "—"}</td>
      <td className={styles.pickQty}>
        {pick.quantity} {pick.uom}
      </td>
      <td className={styles.pickStatus}>
        <PickStatusCell pick={pick} t={t} />
      </td>
    </tr>
  );
}

/**
 * The body once `TaskDetail` has loaded: the meta grid and the pick lines
 * table.
 *
 * The design this drawer is built from also shows an Activity timeline below
 * the table. It is omitted, though no longer for want of data: `ListTaskHistory`
 * carries every device action as a `TaskPickEvent` with its own `DeviceAction`
 * payload, which is what a timeline would be drawn from. What it does not carry
 * is the vocabulary to render one - each payload arm is a different shape with
 * a different sentence to write, and a timeline that renders the arms it knows
 * and drops the rest is worse than no timeline, because there is no way to tell
 * a quiet task from a task full of events nobody taught this screen to say.
 * The Status column below needs none of that: it reads the fulfillment and the
 * event *count*, and never opens a payload.
 */
function DrawerBody({
  task,
  picks,
  params,
  t,
}: {
  task: Task;
  picks: PickLine[];
  params: WarehouseParams;
  t: TFunction;
}) {
  return (
    <div className={styles.body}>
      <div className={styles.metaGrid}>
        <MetaField
          label={t("tasks.taskDetail.drawer.plan")}
          value={task.planName}
        />
        <MetaField
          label={t("tasks.taskDetail.drawer.route")}
          value={
            task.routeCodes.length > 0
              ? task.routeCodes.join(", ")
              : t("tasks.row.none")
          }
        />
        <MetaField
          label={t("tasks.taskDetail.drawer.selector")}
          value={
            task.kind === "activated"
              ? (task.selectorName ?? t("tasks.row.unassigned"))
              : t("tasks.row.none")
          }
        />
        <MetaField
          label={t("tasks.taskDetail.drawer.lines")}
          value={task.lineCount}
        />
        {/*
          Selector, Progress and Started each render the shared em-dash for a
          planned task: none of the three is a field `Task`'s `planned` arm
          carries (see its docblock in `types.ts`) - there is no holder, no
          done-pick count and no claim time to report for a task nobody has
          released, and drawing "0/n" or "Not started" here would answer those
          questions with a value rather than admit there is none yet.
        */}
        <MetaField
          label={t("tasks.taskDetail.drawer.progress")}
          value={
            task.kind === "activated"
              ? `${task.picksDone}/${task.lineCount}`
              : t("tasks.row.none")
          }
        />
        <MetaField
          label={t("tasks.taskDetail.drawer.started")}
          value={
            task.kind === "activated"
              ? task.startedAt
                ? formatStarted(task.startedAt)
                : t("tasks.taskDetail.drawer.notStarted")
              : t("tasks.row.none")
          }
        />
      </div>

      <div className={styles.sectionHeading}>
        {t("tasks.taskDetail.drawer.pickLinesHeading")}
      </div>
      <div className={styles.pickTableWrap}>
        <table className={styles.pickTable}>
          <thead>
            <tr>
              <th className={styles.thFaint}>
                {t("tasks.taskDetail.drawer.table.seq")}
              </th>
              <th>{t("tasks.taskDetail.drawer.table.item")}</th>
              <th>{t("tasks.taskDetail.drawer.table.slot")}</th>
              <th>{t("tasks.taskDetail.drawer.table.qty")}</th>
              <th className={styles.thStatus}>
                {t("tasks.taskDetail.drawer.table.status")}
              </th>
            </tr>
          </thead>
          <tbody>
            {picks.map((pick) => (
              <PickLineRow key={pick.id} pick={pick} params={params} t={t} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TaskDetailDrawer({
  orgId,
  planId,
  taskId,
  task,
  params,
  onClose,
  t,
}: {
  orgId: string | null;
  planId: string;
  taskId: number;
  /** The route params links out of the drawer are built from. */
  params: WarehouseParams;
  task: Task;
  onClose: () => void;
  t: TFunction;
}) {
  const state = fromQuery<TaskDetail>(
    useTaskDetail({ orgId, planId, taskId, task }),
  );

  return (
    <ModalOverlay
      isOpen={true}
      isDismissable={true}
      className={styles.overlay}
      data-test-role="task-detail-scrim"
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <RACModal className={styles.panel} data-test-role="task-detail-drawer">
        <Dialog
          className={styles.dialog}
          aria-label={t("tasks.taskDetail.drawer.title", { id: task.id })}
        >
          <div className={styles.header}>
            <div className={styles.headerText}>
              <div className={styles.titleRow}>
                <h2 className={styles.title}>
                  {t("tasks.taskDetail.drawer.title", { id: task.id })}
                </h2>
                {task.kind === "activated" ? (
                  <TaskStatusTag status={task.status} t={t} />
                ) : (
                  <NotReleasedMark t={t} />
                )}
              </div>
              <div className={styles.sub}>
                {task.routeCodes.length > 0
                  ? task.routeCodes.join(", ")
                  : t("tasks.row.none")}
              </div>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              aria-label={t("tasks.taskDetail.drawer.close")}
              onClick={onClose}
              data-test-role="task-detail-close"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {(() => {
            switch (state.kind) {
              case "loading":
                return (
                  <div
                    className={styles.loading}
                    data-test-role="task-detail-loading"
                  >
                    <Loader type="inline" />
                  </div>
                );
              case "failed":
                return (
                  <div
                    className={styles.error}
                    data-test-role="task-detail-failed"
                  >
                    <AppError
                      description={t("tasks.taskDetail.drawer.failed")}
                      resolution={t("tasks.failureSupport")}
                    />
                  </div>
                );
              case "loaded":
                return (
                  <DrawerBody
                    task={task}
                    picks={state.value.picks}
                    params={params}
                    t={t}
                  />
                );
            }
          })()}
        </Dialog>
      </RACModal>
    </ModalOverlay>
  );
}
